import { Alert, Button, Card, Group, Modal, Stack, Switch, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { describeRpcError } from "@how/app-kit";
import { semanticColor } from "@how/design-system";
import {
  useMarkAsked,
  useMyScheduleEverywhere,
  useSetBusySharing,
  type MyScheduleEverywhere,
} from "./my-schedule-queries";
import { listChurchNames } from "./schedule-model";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? semanticColor("warning") : semanticColor("danger"),
    title,
    message: described.message,
  });
}

/**
 * Sharing busy times (ADR 0066, moved by ADR 0070 R8): the always-visible state and switch on My schedule. With fewer
 * than two churches it still shows and still switches, and says it applies only from two churches on.
 */
export function SharingCard({ data }: { data: MyScheduleEverywhere }) {
  const set = useSetBusySharing();
  const choice = data.sharing.choice;
  const state =
    choice === true
      ? "On: churches that schedule you see “Busy at another church at this time”, never which church or what."
      : choice === false
        ? "Off: your churches see nothing."
        : "Not decided yet: your churches see nothing";
  return (
    <Card withBorder padding="md" component="section" id="privacy" aria-labelledby="sharing-title">
      <Stack gap="xs">
        <Title order={2} size="h4" id="sharing-title">
          Sharing busy times
        </Title>
        <Text size="sm">{state}</Text>
        {data.churches.length >= 2 ? null : (
          <Text size="sm" c="dimmed">
            Applies only when you serve at two or more churches.
          </Text>
        )}
        <Switch
          label="Share busy times"
          checked={choice === true}
          disabled={set.isPending}
          onChange={(e) => {
            set.mutate(e.currentTarget.checked, {
              onError: (error) => {
                notifyError("Could not change sharing", error);
              },
            });
          }}
        />
      </Stack>
    </Card>
  );
}

/**
 * Asked once, anywhere in the portal (ADR 0070 R7; ADR 0066 R5): only while the person serves at two or more churches,
 * has not chosen, and was never asked. The buttons record the choice; closing records only that it was asked.
 */
export function AskBusySharingOnce() {
  const schedule = useMyScheduleEverywhere();
  const set = useSetBusySharing();
  const asked = useMarkAsked();
  const [dismissed, setDismissed] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const data = schedule.data;
  const due =
    data !== undefined &&
    data.churches.length >= 2 &&
    data.sharing.choice === null &&
    data.sharing.askedAt === null;
  const churches = listChurchNames((data?.churches ?? []).map((c) => c.name));
  const choose = (share: boolean) => {
    setFailure(null);
    set.mutate(share, {
      onSuccess: () => {
        setDismissed(true);
      },
      onError: (error) => {
        notifyError("Could not save your choice", error);
        setFailure(describeRpcError(error).message);
      },
    });
  };
  return (
    <Modal
      opened={due && !dismissed}
      onClose={() => {
        // While the choice is saving, closing would race it (Minor 7): the save decides, then the dialog closes.
        if (set.isPending) return;
        setDismissed(true);
        asked.mutate(undefined, {
          onError: (error) => {
            notifyError("Could not save", error);
          },
        });
      }}
      closeButtonProps={{ "aria-label": "Close" }}
      title="Share when you're busy?"
      radius="lg"
    >
      <Stack gap="md">
        <Text size="sm">
          {`You serve at ${churches}. Let each of them see when you're already busy at another church, so they don't double-book you? It shows 'busy' only, never which church or what.`}
        </Text>
        {failure !== null ? (
          <Alert color={semanticColor("danger")} role="alert">
            {failure}
          </Alert>
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            disabled={set.isPending}
            onClick={() => {
              choose(false);
            }}
          >
            No, keep them separate
          </Button>
          <Button
            disabled={set.isPending}
            onClick={() => {
              choose(true);
            }}
          >
            Yes, share busy times
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
